import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import { marked } from 'marked';
import { useEffect } from 'react';
import { updatePageMeta, addStructuredData, generateBlogPostStructuredData } from '../../utils/seo';

marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: true,
  mangle: false,
  sanitize: false,
});

const post = {
  "slug": "valentines-day-gift-exchanges",
  "title": "Valentine's Day Gift Exchanges: Beyond Couples",
  "excerpt": "Galentine's Day, Palentine's Day, and classroom exchanges. Celebrate love and friendship with creative Valentine's gift ideas.",
  "date": "2025-02-01",
  "readTime": "6 min read",
  "category": "Festivals",
  "image": "💝",
  "heroImage": "https://images.unsplash.com/photo-1518199266791-5375a83190b7?w=1200&h=600&fit=crop",
  "author": "SwapGifts Editorial Team",
  "content": "# Valentine's Day Gift Exchanges: Beyond Couples\n\nValentine's Day isn't just for romantic partners! Modern celebrations include Galentine's Day, Palentine's Day, classroom exchanges, and more.\n\n## Galentine's Day (February 13th)\n\nPopularized by the TV show \"Parks and Recreation,\" Galentine's Day celebrates female friendships.\n\n**Gift Exchange Ideas**:\n- Secret Sister exchange ($20-30)\n- Spa products and self-care items\n- Jewelry or accessories\n- Wine or chocolate gift sets\n- Brunch or dinner out together\n\n## Palentine's Day\n\nA celebration of platonic friendships of all genders.\n\n**Group Activity Ideas**:\n- Potluck dinner with White Elephant exchange\n- Game night with small gift swaps\n- Movie marathon with themed snacks\n- Adventure outing (hiking, escape room, etc.)\n\n## Classroom Valentine's Exchanges\n\nFor elementary school celebrations:\n\n**Traditional Approach**:\n- Each child brings valentines for every classmate\n- Include candy or small toys\n- Sign each card personally\n\n**Allergen-Free Options**:\n- Stickers\n- Pencils or erasers\n- Temporary tattoos\n- Bookmarks\n- Small toys (bouncy balls, bubbles)\n\n**DIY Valentine Ideas**:\n- Handmade cards with photos\n- Friendship bracelets\n- Origami hearts\n- Decorated cookies (check for allergies!)\n\n## Office Valentine's Exchanges\n\nKeep it professional and inclusive:\n\n**Secret Valentine**:\n- Set low budget ($5-10)\n- Focus on treats and office-appropriate items\n- Make participation optional\n- Include all team members who want to join\n\n**Gift Ideas**:\n- Gourmet chocolates\n- Coffee or tea\n- Desk plants\n- Funny or motivational desk signs\n- Gift cards to lunch spots\n\n## Family Valentine's Exchanges\n\nCelebrate love within the family:\n\n**For Kids**:\n- Small toys or books\n- Special breakfast or dinner\n- Movie night with popcorn and candy\n- Handmade coupons for activities\n\n**For Adults**:\n- Draw names for Valentine's week\n- Daily small surprises leading up to the 14th\n- Family dinner with special dessert\n\n## Budget-Friendly Valentine's Gifts\n\nUnder $20:\n- Homemade baked goods\n- Photo frame with meaningful picture\n- Succulent or small plant\n- Personalized playlist or mixtape\n- Handwritten letter or poem\n- Favorite candy or snacks\n- Movie rental and popcorn\n- Cozy socks or candles\n\n## Making It Special\n\n**Presentation Matters**:\n- Use red, pink, or white wrapping\n- Add hearts, ribbons, or flowers\n- Include a handwritten note\n- Create a card, even if it's simple\n\n**Thoughtful Touches**:\n- Reference inside jokes\n- Choose gifts based on their interests\n- Include a meaningful memory\n- Plan an experience together\n\n## Conclusion\n\nValentine's Day is about celebrating all forms of love - romantic, platonic, familial, and self-love. A thoughtful gift exchange brings joy to everyone involved."
};

export default function ValentinesDayGiftExchangesPage() {
  useEffect(() => {
    updatePageMeta({
      title: `${post.title} | SwapGifts Blog`,
      description: post.excerpt,
      ogTitle: post.title,
      ogDescription: post.excerpt,
      ogImage: post.heroImage || 'https://swapgifts.store/default-blog-image.jpg',
      canonicalUrl: `https://swapgifts.store/blog/${post.slug}`,
      keywords: `gift exchange, ${post.category.toLowerCase()}, secret santa, ${post.title.toLowerCase()}`
    });

    addStructuredData(generateBlogPostStructuredData({
      title: post.title,
      excerpt: post.excerpt,
      date: post.date,
      author: post.author || 'SwapGifts Editorial Team',
      heroImage: post.heroImage,
      slug: post.slug
    }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <a
          href="/blog"
          className="inline-flex items-center gap-2 text-green-600 font-medium hover:underline mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </a>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {post.heroImage && (
            <div className="w-full h-96 overflow-hidden">
              <img
                src={post.heroImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8 md:p-12">
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                {post.category}
              </span>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {post.title}
            </h1>

            {post.author && (
              <p className="text-sm text-gray-500 mb-6">
                By <span className="font-medium text-gray-700">{post.author}</span>
              </p>
            )}

            <p className="text-xl text-gray-600 mb-8 leading-relaxed border-b border-gray-200 pb-8">
              {post.excerpt}
            </p>

            <div className="flex items-center gap-4 pb-8 mb-8 border-b border-gray-200">
              <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-gray-900 prose-headings:mt-8 prose-headings:mb-4
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-green-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-ul:my-6 prose-ul:space-y-2
                prose-ol:my-6 prose-ol:space-y-2
                prose-li:text-gray-700 prose-li:leading-relaxed
                prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm"
              dangerouslySetInnerHTML={{ __html: marked.parse(post.content) }}
            />
          </div>

          <div className="mt-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to organize your gift exchange?</h2>
            <p className="text-green-100 mb-6">
              Put these tips into action and create your exchange in minutes
            </p>
            <a
              href="/create"
              className="inline-block px-8 py-3 bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Start Drawing Names
            </a>
          </div>
        </div>
      </article>
    </div>
  );
}
